if (!customElements.get('aestara-cohesive-sets')) {
  customElements.define(
    'aestara-cohesive-sets',
    class AestaraCohesiveSets extends HTMLElement {
      connectedCallback() {
        document.body.classList.add('has-acs-matching-sets');
        this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency: 'AUD',
        });

        this.familyElements = [...this.querySelectorAll('[data-acs-family]')];
        this.familyElements.forEach((family) => this.setupFamily(family));
        this.observeFamilies();
        this.querySelectorAll('[data-acs-track]').forEach((link) => {
          link.addEventListener('click', () => this.track(link.dataset.acsTrack));
        });
        this.querySelectorAll('[data-acs-family-link]').forEach((link) => {
          link.addEventListener('click', () => this.track('matching_family_jump', { family: link.dataset.acsFamilyLink }));
        });
      }

      setupFamily(family) {
        const state = {
          family,
          name: family.dataset.acsFamilyName,
          preferredFinish: this.normalizeFinish(family.dataset.acsPreferredFinish),
          pull: this.readProduct(family, 'pull'),
          knob: this.readProduct(family, 'knob'),
          selectedFinish: { pull: '', knob: '' },
          selectedFinishLabel: { pull: '', knob: '' },
          selectedSize: '',
          pullVariant: null,
          knobVariant: null,
          adding: false,
        };

        if (!state.pull || !state.knob) return;

        state.pullFinishIndex = this.findOptionIndex(state.pull, /finish|colou?r/i, 0);
        state.knobFinishIndex = this.findOptionIndex(state.knob, /finish|colou?r/i, 0);
        state.pullSizeIndex = this.findOptionIndex(state.pull, /size|hole|centre|center|length/i, -1);
        state.availableFinishes = {
          pull: this.getAvailableFinishes(state.pull, state.pullFinishIndex),
          knob: this.getAvailableFinishes(state.knob, state.knobFinishIndex),
        };

        state.sharedFinishes = this.getSharedFinishes(
          state.availableFinishes.pull,
          state.availableFinishes.knob,
        );

        const finishes = family.querySelector('[data-acs-finishes="shared"]');
        state.sharedFinishes.forEach((finish) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'acs-finish';
          button.dataset.finish = finish.key;
          button.dataset.finishRole = 'shared';
          button.setAttribute('aria-pressed', 'false');
          button.innerHTML = `<span class="acs-finish__swatch acs-finish__swatch--${finish.tone}" aria-hidden="true"></span><span>${this.escapeHtml(finish.label)}</span>`;
          button.addEventListener('click', () => this.selectSharedFinish(state, finish, true));
          finishes?.append(button);
        });

        const paletteContainer = family.querySelector('[data-acs-palette-items]');
        const paletteHeading = family.querySelector('[data-acs-palette-heading]');
        if (paletteHeading) {
          const countLabel = state.sharedFinishes.length === 1 ? '1 matching finish' : `${state.sharedFinishes.length} matching finishes`;
          paletteHeading.textContent = countLabel;
        }

        state.sharedFinishes.forEach((finish) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'acs-palette__item';
          button.dataset.acsBuildFinish = '';
          button.dataset.finish = finish.key;
          button.setAttribute('aria-label', `Build the ${state.name} pull and knob in ${finish.label}`);
          button.innerHTML = `<span class="acs-finish__swatch acs-finish__swatch--${finish.tone}" aria-hidden="true"></span><span><strong>${this.escapeHtml(finish.label)}</strong><small>Build this pair</small></span>`;
          button.addEventListener('click', () => {
            this.selectSharedFinish(state, finish, true);
            const shop = family.querySelector('[data-acs-shop]');
            if (shop) shop.open = true;
            this.track('matching_family_build_exact_pair', {
              family: state.name,
              finish: finish.label,
            });
          });
          paletteContainer?.append(button);
        });

        if (!state.sharedFinishes.length) {
          const message = document.createElement('p');
          message.className = 'acs-palette__empty';
          message.textContent = 'This matching family is temporarily unavailable online.';
          paletteContainer?.append(message);
        }

        const shop = family.querySelector('[data-acs-shop]');
        shop?.addEventListener('toggle', () => {
          if (shop.open) this.track('matching_family_shop_open', { family: state.name });
        });

        family.querySelector('[data-acs-size]')?.addEventListener('change', (event) => {
          state.selectedSize = event.target.value;
          this.updateVariants(state);
          this.track('matching_family_size_selected', { family: state.name, size: state.selectedSize });
        });

        family.querySelectorAll('[data-acs-quantity]').forEach((input) => {
          input.addEventListener('change', () => {
            this.sanitizeQuantity(input);
            this.updateSummary(state);
          });
          input.addEventListener('input', () => this.updateSummary(state));
        });

        family.querySelector('[data-acs-add]')?.addEventListener('click', () => this.addSet(state));
        family.querySelectorAll('[data-acs-piece], [data-acs-detail-link]').forEach((link) => {
          link.addEventListener('click', () => {
            const role = link.dataset.acsPiece || link.dataset.acsDetailLink;
            this.track('matching_family_product_click', { family: state.name, product_role: role });
          });
        });
        const initialFinish = state.sharedFinishes.find((finish) => finish.key === state.preferredFinish) || state.sharedFinishes[0];
        if (initialFinish) this.selectSharedFinish(state, initialFinish, false);
      }

      observeFamilies() {
        if (!('IntersectionObserver' in window)) return;
        const seen = new WeakSet();
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || seen.has(entry.target)) return;
            seen.add(entry.target);
            this.track('matching_family_view', { family: entry.target.dataset.acsFamilyName });
            observer.unobserve(entry.target);
          });
        }, { threshold: 0.45 });
        this.familyElements.forEach((family) => observer.observe(family));
      }

      readProduct(family, role) {
        try {
          return JSON.parse(family.querySelector(`[data-acs-product-json="${role}"]`)?.textContent || '{}');
        } catch (error) {
          console.error('Aestara cohesive sets: product data could not be read.', error);
          return null;
        }
      }

      findOptionIndex(product, pattern, fallback) {
        const names = (product.options || []).map((option) => (typeof option === 'string' ? option : option?.name || ''));
        const index = names.findIndex((name) => pattern.test(name));
        return index < 0 ? fallback : index;
      }

      getAvailableFinishes(product, finishIndex) {
        const finishes = new Map();
        product.variants.forEach((variant) => {
          if (variant.available === false) return;
          const label = this.optionValue(variant, finishIndex);
          const key = this.normalizeFinish(label);
          if (key && !finishes.has(key)) finishes.set(key, { key, label });
        });
        return [...finishes.values()];
      }

      getSharedFinishes(pullFinishes, knobFinishes) {
        const preferredOrder = [
          'Brushed + Polished Copper (PVD)',
          'Brushed + Matte Gold (PVD)',
          'Polished Gold (PVD) + Antique Brass',
          'Polished Black + Matte Black',
        ].map((label) => this.normalizeFinish(label));
        const knobFinishKeys = new Set(knobFinishes.map((finish) => finish.key));

        return pullFinishes
          .filter((finish) => knobFinishKeys.has(finish.key))
          .map((finish) => ({ ...finish, tone: this.finishTone(finish.label) }))
          .sort((a, b) => {
            const aIndex = preferredOrder.indexOf(a.key);
            const bIndex = preferredOrder.indexOf(b.key);
            if (aIndex < 0 && bIndex < 0) return 0;
            if (aIndex < 0) return 1;
            if (bIndex < 0) return -1;
            return aIndex - bIndex;
          });
      }

      setSharedFinishSelection(state, finish) {
        state.selectedFinish.pull = finish.key;
        state.selectedFinish.knob = finish.key;
        state.selectedFinishLabel.pull = finish.label;
        state.selectedFinishLabel.knob = finish.label;
        state.family.querySelectorAll('[data-finish-role="shared"], [data-acs-build-finish]').forEach((button) => {
          const selected = button.dataset.finish === finish.key;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
      }

      selectSharedFinish(state, finish, shouldTrack) {
        this.setSharedFinishSelection(state, finish);
        this.populateSizes(state);
        this.updateVariants(state);
        if (shouldTrack) this.track('matching_family_finish_selected', {
          family: state.name,
          product_role: 'set',
          finish: finish.label,
          synced_knob: true,
        });
      }

      populateSizes(state) {
        const sizeSelect = state.family.querySelector('[data-acs-size]');
        const sizeControl = state.family.querySelector('[data-acs-size-control]');
        if (!sizeSelect || state.pullSizeIndex < 0) {
          if (sizeControl) sizeControl.hidden = true;
          state.selectedSize = '';
          return;
        }

        const sizes = [...new Set(state.pull.variants
          .filter((variant) => variant.available !== false && this.normalizeFinish(this.optionValue(variant, state.pullFinishIndex)) === state.selectedFinish.pull)
          .map((variant) => this.optionValue(variant, state.pullSizeIndex))
          .filter(Boolean))];

        const previous = state.selectedSize;
        sizeSelect.replaceChildren(...sizes.map((size) => {
          const option = document.createElement('option');
          option.value = size;
          option.textContent = size;
          return option;
        }));
        state.selectedSize = sizes.includes(previous) ? previous : sizes[0] || '';
        sizeSelect.value = state.selectedSize;
        if (sizeControl) sizeControl.hidden = sizes.length <= 1;
      }

      updateVariants(state) {
        state.pullVariant = state.pull.variants.find((variant) => {
          const finishMatches = this.normalizeFinish(this.optionValue(variant, state.pullFinishIndex)) === state.selectedFinish.pull;
          const sizeMatches = state.pullSizeIndex < 0 || this.optionValue(variant, state.pullSizeIndex) === state.selectedSize;
          return variant.available !== false && finishMatches && sizeMatches;
        }) || null;
        state.knobVariant = state.knob.variants.find((variant) => {
          return variant.available !== false && this.normalizeFinish(this.optionValue(variant, state.knobFinishIndex)) === state.selectedFinish.knob;
        }) || null;

        this.updatePiece(state, 'pull');
        this.updatePiece(state, 'knob');
        this.updateSummary(state);
      }

      updatePiece(state, role) {
        const product = state[role];
        const variant = state[`${role}Variant`];
        const image = state.family.querySelector(`[data-acs-image="${role}"]`);
        const price = state.family.querySelector(`[data-acs-price="${role}"]`);
        const link = state.family.querySelector(`[data-acs-piece="${role}"]`);
        const detailLink = state.family.querySelector(`[data-acs-detail-link="${role}"]`);
        const imageUrl = this.getVariantImage(product, variant);
        const productUrl = product.url || `/products/${product.handle}`;

        if (image && imageUrl) image.src = this.resizeImage(imageUrl, 420);
        if (price && variant) price.textContent = this.formatMoney(variant.price);
        if (link && variant) link.href = `${productUrl}?variant=${variant.id}`;
        if (detailLink && variant) {
          detailLink.href = `${productUrl}?variant=${variant.id}`;
          detailLink.setAttribute('aria-label', `View ${product.title} in ${state.selectedFinishLabel[role]}`);
        }
      }

      updateSummary(state) {
        const ready = Boolean(state.pullVariant && state.knobVariant);
        const total = ready
          ? Number(state.pullVariant.price) * this.quantity(state, 'pull') + Number(state.knobVariant.price) * this.quantity(state, 'knob')
          : 0;
        const totalNode = state.family.querySelector('[data-acs-total]');
        const summaryPrice = state.family.querySelector('[data-acs-summary-price]');
        const add = state.family.querySelector('[data-acs-add]');
        if (totalNode) totalNode.textContent = ready ? this.formatMoney(total) : '—';
        if (summaryPrice) summaryPrice.textContent = ready ? `${this.formatMoney(total)} for pull + knob` : 'Pull + knob added together';
        if (add) add.disabled = !ready || state.adding;
      }

      quantity(state, role) {
        const value = Number.parseInt(state.family.querySelector(`[data-acs-quantity="${role}"]`)?.value || '1', 10);
        return Math.min(99, Math.max(1, Number.isFinite(value) ? value : 1));
      }

      sanitizeQuantity(input) {
        const value = Number.parseInt(input.value || '1', 10);
        input.value = String(Math.min(99, Math.max(1, Number.isFinite(value) ? value : 1)));
      }

      async addSet(state) {
        if (state.adding || !state.pullVariant || !state.knobVariant) return;
        state.adding = true;
        this.setLoading(state, true);
        this.setStatus(state, '');

        const pullQuantity = this.quantity(state, 'pull');
        const knobQuantity = this.quantity(state, 'knob');
        const setProperties = {
          _aestara_matching_family: state.name,
          _aestara_pull_finish: state.selectedFinishLabel.pull,
          _aestara_knob_finish: state.selectedFinishLabel.knob,
        };
        const items = [
          { id: Number(state.pullVariant.id), quantity: pullQuantity, properties: setProperties },
          { id: Number(state.knobVariant.id), quantity: knobQuantity, properties: setProperties },
        ];

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };
        if (cart?.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        try {
          const configuredUrl = window.routes?.cart_add_url || '/cart/add';
          const cartAddUrl = configuredUrl.endsWith('.js') ? configuredUrl : `${configuredUrl}.js`;
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
          });
          const text = await response.text();
          let result;
          try {
            result = JSON.parse(text);
          } catch (error) {
            throw new Error('Shopify returned an unexpected cart response. Please try again.');
          }
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'This set could not be added.');

          const value = (Number(state.pullVariant.price) * pullQuantity + Number(state.knobVariant.price) * knobQuantity) / 100;
          this.track('matching_family_add_to_cart', {
            family: state.name,
            pull_finish: state.selectedFinishLabel.pull,
            knob_finish: state.selectedFinishLabel.knob,
            pull_variant_id: state.pullVariant.id,
            pull_quantity: pullQuantity,
            knob_variant_id: state.knobVariant.id,
            knob_quantity: knobQuantity,
            value,
            currency: 'AUD',
          });
          this.setStatus(state, `${state.name} pull and knob added to cart.`, true);
          if (cart?.renderContents) {
            cart.classList.remove('is-empty');
            cart.renderContents(result);
          } else {
            window.location.assign(window.routes?.cart_url || '/cart');
          }
        } catch (error) {
          this.setStatus(state, error.message || 'Something went wrong. Please try again.');
        } finally {
          state.adding = false;
          this.setLoading(state, false);
          this.updateSummary(state);
        }
      }

      setLoading(state, loading) {
        const button = state.family.querySelector('[data-acs-add]');
        const label = state.family.querySelector('[data-acs-add-label]');
        button?.classList.toggle('loading', loading);
        button?.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        if (label) label.textContent = loading ? 'Adding both pieces…' : 'Add pull + knob to cart';
      }

      setStatus(state, message, success = false) {
        const status = state.family.querySelector('[data-acs-status]');
        if (!status) return;
        status.textContent = message;
        status.classList.toggle('is-success', success);
      }

      optionValue(variant, index) {
        if (index < 0) return '';
        return variant.options?.[index] || variant[`option${index + 1}`] || '';
      }

      normalizeFinish(value) {
        return String(value || '').toLowerCase().replace(/\(pvd\)/g, '').replace(/pvd/g, '').trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }

      finishTone(label) {
        const value = String(label || '').toLowerCase();
        if (value.includes('black')) return 'black';
        if (value.includes('copper') || value.includes('rose')) return 'copper';
        if (value.includes('chrome') || value.includes('satin')) return 'silver';
        if (value.includes('bronze') || value.includes('antique')) return 'bronze';
        return 'gold';
      }

      getVariantImage(product, variant) {
        const featured = variant?.featured_image;
        if (typeof featured === 'string') return featured;
        if (featured?.src) return featured.src;
        if (typeof product?.featured_image === 'string') return product.featured_image;
        if (product?.featured_image?.src) return product.featured_image.src;
        return product?.images?.[0] || '';
      }

      resizeImage(url, width) {
        if (!url) return '';
        const absolute = url.startsWith('//') ? `https:${url}` : url;
        try {
          const parsed = new URL(absolute, window.location.origin);
          const isShopifyCdn = parsed.hostname.endsWith('cdn.shopify.com') || parsed.pathname.includes('/cdn/shop/files/');
          if (!isShopifyCdn) return absolute;
          parsed.searchParams.set('width', String(width));
          return parsed.toString();
        } catch (error) {
          return absolute;
        }
      }

      formatMoney(cents) {
        return this.moneyFormatter.format(Number(cents || 0) / 100);
      }

      escapeHtml(value) {
        const node = document.createElement('span');
        node.textContent = value;
        return node.innerHTML;
      }

      track(eventName, detail = {}) {
        const payload = { event: eventName, source: 'matching_sets_v3', ...detail };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        if (typeof window.clarity === 'function') {
          window.clarity('event', eventName);
          Object.entries(detail).forEach(([key, value]) => {
            if (value !== undefined && value !== null) window.clarity('set', `acs_${key}`, String(value));
          });
        }
        this.dispatchEvent(new CustomEvent('aestara:matching-family-event', { bubbles: true, detail: payload }));
      }
    }
  );
}
